/**
 * Con qué plan marcado arranca /contratar.
 *
 * Hasta el 6-oct-2026 arrancaba SIEMPRE en Pro (`useState('pro')`): quien pulsaba «Comenzar con
 * Starter» en la landing aterrizaba con Pro elegido, y si no se fijaba, pagaba el doble.
 *
 * Dos fuentes, por orden:
 *   1. `?plan=starter|pro` en la URL. Lo pone la landing en los dos botones de la tabla de
 *      precios, y sobrevive al login porque `/contratar` sin sesión guarda su destino completo
 *      (`AlLoginConservandoDestino`, como `/addon`).
 *   2. `pux_origin` de la atribución (`pricing-starter` / `pricing-pro`), capturada en el
 *      arranque. Cubre un bundle de landing anterior a este cambio y el caso en que el destino
 *      guardado caducó: la atribución vive en `sessionStorage` toda la pestaña.
 * Sin ninguna de las dos, Pro — que es lo que se marcaba antes y el plan recomendado.
 *
 * Cualquier otro valor se ignora: un `?plan=enterprise` no puede elegir un plan inexistente.
 *
 * CommonJS y pura para probarla desde `functions/__tests__` (los tests de `src/` no corren).
 */
const PLANES = ['starter', 'pro'];
const POR_DEFECTO = 'pro';

function planDeTexto(valor) {
  const v = typeof valor === 'string' ? valor.trim().toLowerCase() : '';
  return PLANES.includes(v) ? v : null;
}

/**
 * @param {{search?: string, atribucion?: object}} entrada
 *   search      `window.location.search` de /contratar
 *   atribucion  lo que devuelve `getAttribution()`
 * @returns {'starter'|'pro'}
 */
function planInicial({ search, atribucion } = {}) {
  let desdeUrl = null;
  try {
    desdeUrl = planDeTexto(new URLSearchParams(search || '').get('plan'));
  } catch {
    desdeUrl = null;
  }
  if (desdeUrl) return desdeUrl;

  const origen = atribucion && typeof atribucion.pux_origin === 'string' ? atribucion.pux_origin : '';
  const m = /^pricing-(starter|pro)$/.exec(origen);
  if (m) return m[1];

  return POR_DEFECTO;
}

/**
 * Igual que `planInicial`, pero dice si el plan lo ELIGIÓ la persona (`?plan=` o un botón de la
 * tabla de precios) o es el de por defecto. El login lo usa para decir «Plan elegido» sólo cuando
 * hubo elección; sin ella dice «Plan sugerido» (caso 3 de las pruebas, 6-oct-2026).
 *
 * @returns {{plan: 'starter'|'pro', elegido: boolean}}
 */
function planInicialConOrigen({ search, atribucion } = {}) {
  const plan = planInicial({ search, atribucion });
  let desdeUrl = null;
  try { desdeUrl = planDeTexto(new URLSearchParams(search || '').get('plan')); } catch { desdeUrl = null; }
  const origen = atribucion && typeof atribucion.pux_origin === 'string' ? atribucion.pux_origin : '';
  const elegido = !!desdeUrl || /^pricing-(starter|pro)$/.test(origen);
  return { plan, elegido };
}

/**
 * El plan con el que arranca /contratar cuando la persona VUELVE (de Stripe, de un reload): el
 * que guardó al pulsar «Continuar al pago» (`clientes.plan`) gana sobre el origen de la landing,
 * porque es su última elección explícita; sólo un `?plan=` nuevo en la URL le gana a ése.
 * (Caso 9 de las pruebas, 6-oct-2026: volvía a Pro aunque había elegido Starter.)
 */
function planAlVolver({ search, atribucion, guardado } = {}) {
  let desdeUrl = null;
  try { desdeUrl = planDeTexto(new URLSearchParams(search || '').get('plan')); } catch { desdeUrl = null; }
  if (desdeUrl) return desdeUrl;
  const g = planDeTexto(guardado);
  if (g) return g;
  return planInicial({ search, atribucion });
}

/** «Nombre completo» prerrellenado: lo guardado en `clientes`, o el `displayName` de Google. */
function nombreInicial({ guardado, displayName } = {}) {
  const g = typeof guardado === 'string' ? guardado.trim() : '';
  if (g) return g;
  const d = typeof displayName === 'string' ? displayName.trim() : '';
  return d;
}

module.exports = { planInicial, planInicialConOrigen, planAlVolver, nombreInicial, planDeTexto, PLANES, POR_DEFECTO };
